import React, { useState } from "react";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { CardHeader, CardTitle } from "./ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Trash2, AlertTriangle, ArrowUpDown } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import fetchWithAuth from "../fetchWithAuth";
import { useBulkRowSelection, SelectionCheckbox, BulkDeleteBar } from "./BulkRowSelection";

export default function BlacklistPagesTable({ projectId, groupId, pages = [], onChange, onBulkChange }) {
  const [newUrl, setNewUrl] = useState("");
  const [error, setError] = useState("");
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [pageToDelete, setPageToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [localPages, setLocalPages] = useState(pages);
  const selection = useBulkRowSelection(localPages, setLocalPages, { projectId, resource: "blacklist", onChange: onBulkChange });
  const [sorting, setSorting] = useState({ column: "createdAt", direction: "desc" });
  const [isDeleteAllDialogOpen, setIsDeleteAllDialogOpen] = useState(false);
  const [isDeletingAll, setIsDeletingAll] = useState(false);

  // Обновляем localPages при изменении props.pages
  React.useEffect(() => {
    setLocalPages(pages);
  }, [pages]);

  // Сортировка
  const handleSort = (column) => {
    setSorting(prev => ({
      column,
      direction: prev.column === column && prev.direction === "asc" ? "desc" : "asc"
    }));
  };

  const getSortedPages = () => {
    const sorted = [...localPages];
    switch (sorting.column) {
      case "blackUrl":
        sorted.sort((a, b) => {
          if (!a.blackUrl) return 1;
          if (!b.blackUrl) return -1;
          if (a.blackUrl < b.blackUrl) return sorting.direction === "asc" ? -1 : 1;
          if (a.blackUrl > b.blackUrl) return sorting.direction === "asc" ? 1 : -1;
          return 0;
        });
        break;
      case "createdAt":
        sorted.sort((a, b) => {
          const av = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const bv = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return sorting.direction === "asc" ? av - bv : bv - av;
        });
        break;
      default:
        break;
    }
    return sorted;
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    setError("");
    if (!newUrl) {
      setError("Введите URL");
      return;
    }
    try {
      // Разделяем строки и обрабатываем каждый URL
      const urls = newUrl.split('\n').filter(url => url.trim());
      
      if (urls.length === 0) {
        setError("Введите URL");
        return;
      }
      
      // Добавляем каждый URL
      for (const url of urls) {
        const res = await fetchWithAuth(`/api/project/${projectId}/blacklist`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ urls: url.trim() }),
        });
        
        if (!res.ok) {
          const data = await res.json();
          setError(`Ошибка при добавлении URL ${url}: ${data.error || "Ошибка при добавлении"}`);
          return;
        }
      }
      
      // Получаем новые данные и обновляем локальное состояние вместо вызова onChange
      const updatedDataRes = await fetchWithAuth(`/api/project/${projectId}`);
      const updatedData = await updatedDataRes.json();
      setLocalPages(updatedData.blacklistPages || []);
      
      setNewUrl("");
      // Не вызываем onChange, чтобы избежать перезагрузки страницы
      // onChange && onChange();
    } catch {
      setError("Ошибка при добавлении");
    }
  };

  const openDeleteDialog = (page) => {
    setPageToDelete(page);
    setIsDeleteDialogOpen(true);
  };

  const handleDelete = () => {
    if (!pageToDelete) return;
    
    // Обновляем локальное состояние без перезагрузки всей страницы
    setLocalPages(prevPages => prevPages.filter(p => p.id !== pageToDelete.id));
    
    // Закрываем диалог
    setIsDeleteDialogOpen(false);
    
    // Выполняем удаление на сервере
    fetchWithAuth(`/api/project/${projectId}/blacklist/${pageToDelete.id}`, {
      method: "DELETE",
    })
    .then(response => {
      if (!response.ok) {
        // Проверяем Content-Type ответа
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          return response.json().then(data => {
            throw new Error(data.error || "Ошибка при удалении");
          });
        } else {
          throw new Error("Ошибка при удалении");
        }
      }
      
      // Не уведомляем родительский компонент об изменении, чтобы не перезагружать страницу
      // onChange && onChange();
      setPageToDelete(null);
    })
    .catch(error => {
      console.error("Ошибка при удалении:", error);
      setError(error.message || "Ошибка при удалении");
    });
  };

  // Удалить все вытесняемые страницы
  const handleDeleteAll = async () => {
    setIsDeletingAll(true);
    try {
      const res = await fetchWithAuth(`/api/project/${projectId}/blacklist`, {
        method: "DELETE",
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.error || "Ошибка при удалении всех страниц");
        return;
      }
      setLocalPages([]);
      setIsDeleteAllDialogOpen(false);
    } catch {
      setError("Ошибка при удалении всех страниц");
    } finally {
      setIsDeletingAll(false);
    }
  };

  // Форматирование даты: dd.mm.yyyy
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  };

  return (
    <div className="space-y-4">
      <CardHeader className="px-0 pt-0">
        <CardTitle>Вытесняемые страницы</CardTitle>
      </CardHeader>
      
      <form onSubmit={handleAdd} className="space-y-4">
        <div className="flex gap-4 items-end">
          <div className="flex-1">
            <Textarea
              id="newBlacklistUrl"
              placeholder="Новые URL (каждая строка — отдельный URL)"
              value={newUrl}
              onChange={e => setNewUrl(e.target.value)}
              className="min-h-[100px]"
            />
          </div>
          <Button type="submit">Добавить</Button>
        </div>
        
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}
      </form>
      
      <BulkDeleteBar selection={selection} label="Вытесняемые страницы" />
      <div className="border rounded-md">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[44px]"><SelectionCheckbox checked={selection.all} partial={selection.partial} onChange={selection.toggleAll} disabled={selection.busy || !localPages.length} label="Выбрать все вытесняемые страницы" /></TableHead>
              <TableHead className="w-[50px]">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                  title="Удалить все страницы"
                  onClick={() => setIsDeleteAllDialogOpen(true)}
                  disabled={localPages.length === 0}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </TableHead>
              <TableHead className="w-[50px]">#</TableHead>
              <TableHead>
                <Button
                  variant="ghost"
                  onClick={() => handleSort("blackUrl")}
                  className="flex items-center gap-1 font-medium"
                >
                  URL
                  <ArrowUpDown className="h-4 w-4" />
                </Button>
              </TableHead>
              <TableHead className="w-[150px] text-center">
                <Button
                  variant="ghost"
                  onClick={() => handleSort("createdAt")}
                  className="flex items-center gap-1 font-medium justify-center"
                >
                  Дата добавления
                  <ArrowUpDown className="h-4 w-4" />
                </Button>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {getSortedPages().map((page, idx) => (
              <TableRow key={page.id}>
                <TableCell><SelectionCheckbox checked={selection.selected.has(page.id)} onChange={() => selection.toggle(page.id)} disabled={selection.busy} label={`Выбрать вытесняемую страницу ${page.partialUrl || page.blackUrl}`} /></TableCell>
                <TableCell className="w-[50px]">
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                    onClick={() => openDeleteDialog(page)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TableCell>
                <TableCell className="w-[50px]">{idx + 1}</TableCell>
                <TableCell>
                  <a 
                    href={/^https?:\/\//i.test(page.blackUrl) ? page.blackUrl : `https://${page.blackUrl}`}
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    {page.partialUrl || page.blackUrl}
                  </a>
                </TableCell>
                <TableCell className="w-[150px] text-center">
                  {page.createdAt ? formatDate(new Date(page.createdAt)) : "—"}
                </TableCell>
              </TableRow>
            ))}
            {localPages.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-4 text-gray-500">
                  Нет вытесняемых страниц
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Диалог подтверждения удаления всех */}
      <Dialog open={isDeleteAllDialogOpen} onOpenChange={setIsDeleteAllDialogOpen}>
        <DialogContent className="sm:max-w-[425px] pointer-events-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              Удалить все вытесняемые страницы
            </DialogTitle>
            <DialogDescription>
              Вы уверены, что хотите удалить <strong>все</strong> вытесняемые страницы этого проекта ({localPages.length} шт.)?
              <br />
              <span className="text-red-600">Это действие необратимо.</span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsDeleteAllDialogOpen(false)}
              disabled={isDeletingAll}
            >
              Отмена
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteAll}
              disabled={isDeletingAll}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {isDeletingAll ? "Удаление..." : "Удалить все"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Диалог подтверждения удаления */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[425px] pointer-events-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              Подтверждение удаления
            </DialogTitle>
            <DialogDescription>
              Вы уверены, что хотите удалить эту страницу?
              {pageToDelete && (
                <div className="mt-2 p-2 bg-gray-50 rounded border text-sm break-all">
                  {pageToDelete.blackUrl}
                </div>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setIsDeleteDialogOpen(false)}
            >
              Отмена
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {isDeleting ? "Удаление..." : "Удалить"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
